/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 * Component to pick month range
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @version 1.13.0
 * @since 1.1.0
 */

import { createRef, Fragment, PureComponent, ReactElement } from 'react';

import range from 'lodash/range';
import moment, { Moment } from 'moment';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { makeSelectTranslationCommon } from '../../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../../modules/internationalization/types';
import { EFormatData } from '../../../pages/PageDashboard/types/widgetTypes';
import { IRootReducer } from '../../../reducers/rootReducer';
import { DropDownFilter } from '../index';
// eslint-disable-next-line import/order
import { DropDownButtonAreaTypeEnum, DropDownFilter as DropDownFilterRef } from '../DropDownFilter/DropDownFilter';
import RangeMonthPickerButton from './RangeMonthPickerButton';
import RangeMonthPickerHeader from './RangeMonthPickerHeader';
import RangeMonthPickerItem from './RangeMonthPickerItem';
import RangeMonthPickerPresets from './RangeMonthPickerPresets';
import {
    RangeMonthPickerRowsContainerStyled,
    RangeMonthPickerRowStyled,
    RangeMonthPickerStyled,
} from './styled/RangeMonthPickerStyled';

export class RangeMonthPicker extends PureComponent<IConnectedProps & IRangeMonthPicker> {
    minDate = moment().add(-3, 'year').startOf('month');
    maxDate = moment().endOf('month');
    gridMonths = [[1, 2, 3], [4, 5, 6], [7, 8, 9], [10, 11, 12]];

    dropDownRef = createRef<DropDownFilterRef>();

    state = {
        allowNextYear: false,
        allowPrevYear: false,
        endDate: this.props.rangeEnd || this.maxDate,
        firstClick: true,
        maxDate: this.props.maxDate || this.maxDate,
        minDate: this.props.minDate || this.minDate,
        showYear: moment().get('year'),
        startDate: this.props.rangeStart
            || this.maxDate.clone().add(-2, 'month').startOf('month'), // Clone exclude mutation
    };

    updateAllows = (): void => {
        const allowNextYear = !this.state.maxDate || this.state.maxDate.get('year') > this.state.showYear;
        const allowPrevYear = !this.state.minDate || this.state.minDate.get('year') < this.state.showYear;

        this.setState({
            allowNextYear,
            allowPrevYear,
        });
    };

    componentDidMount(): void {
        this.updateAllows();
    }

    componentDidUpdate(prevProps: Readonly<IConnectedProps & IRangeMonthPicker>): void {
        if (this.props.rangeEnd && this.props.rangeStart &&
            (prevProps.rangeEnd !== this.props.rangeEnd ||
                prevProps.rangeStart !== this.props.rangeStart)) {
            this.setRange(this.props.rangeStart, this.props.rangeEnd);
        }
    }

    setRange = (startDate: Moment, endDate: Moment): void => {
        this.setState({
            firstClick: true,
            showYear: endDate.get('year'),
            startDate,
            endDate,
        }, this.updateAllows);
    };

    selectMonthHandler = (monthNum: number): void => {
        const date = this.getDateFromMonthNum(monthNum).startOf('month');

        if (this.props.isSingle) {
            this.setState({
                firstClick: false,
                startDate: date.endOf('month'),
                endDate: date.endOf('month'),
            });
            this.props.onSelect?.(date);
            if (this.dropDownRef.current) {
                this.dropDownRef.current.handleCloseButtonClick();
            }
            return;
        }
        if (this.state.firstClick) {
            this.setState({
                firstClick: false,
                startDate: date,
                endDate: date,
            });
        } else {
            if (date < this.state.startDate) {
                this.setState({
                    firstClick: true,
                    startDate: date,
                    endDate: this.state.startDate,
                });
            } else {
                this.setState({
                    firstClick: true,
                    endDate: date,
                });
            }
        }
    };

    changeYear = (year: number): void => {
        this.setState({
            showYear: year,
        }, this.updateAllows);
    };

    getDateFromMonthNum = (monthNum: number): Moment => {
        return moment(this.state.showYear, EFormatData.OnlyYearFull).set({
            month: monthNum - 1,
        });
    };

    getSelectedMonthsCount = (startDate: Moment, endDate: Moment): number => {
        return moment(endDate).diff(startDate, 'months') + 1;
    };

    isDisabled = (monthNum: number): boolean => {
        const date = this.getDateFromMonthNum(monthNum);
        return (this.state.minDate && date.isBefore(this.state.minDate, 'month'))
            || (this.state.maxDate && date.isAfter(this.state.maxDate, 'month'));
    };

    isInRange = (monthNum: number): boolean => {
        const date = this.getDateFromMonthNum(monthNum).startOf('month');
        return date.isBetween(this.state.startDate, this.state.endDate, 'month', '[]');
    };

    isRangeStart = (monthNum: number): boolean => {
        const date = this.getDateFromMonthNum(monthNum);
        return date.isSame(this.state.startDate, 'month');
    };

    isRangeEnd = (monthNum: number): boolean => {
        const date = this.getDateFromMonthNum(monthNum);
        return date.isSame(this.state.endDate, 'month');
    };

    onOkClick = (): void => {
        const rangeStart = this.state.startDate;
        const rangeEnd = this.state.firstClick ? this.state.endDate : this.state.startDate;
        this.props.onOkClick?.(rangeStart, rangeEnd.endOf('month'));
    };

    onCloseClick = (): void => {
        this.setRange(this.props.rangeStart
            || this.maxDate.clone().add(-2, 'month').startOf('month'),
            this.props.rangeEnd || this.maxDate);
    };

    renderDropdownBtn = (): ReactElement<typeof RangeMonthPickerButton> =>
        <RangeMonthPickerButton label={this.props.label}/>;

    render() {
        return <Fragment>

            <DropDownFilter
                filterButtonLabel={this.props.label}
                ref={this.dropDownRef}
                renderButton={this.renderDropdownBtn}
                renderButtonAreaType={DropDownButtonAreaTypeEnum.monthPicker}
                renderInBody={true}
                showCancelBtn={!this.props.isSingle}
                translation={this.props.translation.dropDown}
                widthAuto={true}
                onCloseClick={this.props.isSingle ? undefined : this.onCloseClick}
                onOkClick={this.props.isSingle ? undefined : this.onOkClick}
            >
                <RangeMonthPickerStyled>

                    <RangeMonthPickerHeader
                        allowNextYear={this.state.allowNextYear}
                        allowPrevYear={this.state.allowPrevYear}
                        changeYear={this.changeYear}
                        showYear={this.state.showYear}
                        years={range(this.minDate.get('year'), this.maxDate.get('year') + 1)}
                    />

                    <RangeMonthPickerRowsContainerStyled>
                        {this.gridMonths.map(row => (
                            <RangeMonthPickerRowStyled key={row.toString()}>
                                {row.map(monthNum => (
                                    <RangeMonthPickerItem
                                        dataName={`selectMonth${monthNum}`}
                                        disabled={this.isDisabled(monthNum)}
                                        inRange={this.isInRange(monthNum)}
                                        key={monthNum}
                                        rangeEnd={this.isRangeEnd(monthNum)}
                                        rangeStart={this.isRangeStart(monthNum)}
                                        onClickMonth={() => this.selectMonthHandler(monthNum)}
                                    >

                                        {this.props.translation.calendar.months[monthNum - 1]}

                                    </RangeMonthPickerItem>
                                ))}
                            </RangeMonthPickerRowStyled>
                        ))}
                    </RangeMonthPickerRowsContainerStyled>

                    {
                        this.props.withPresets && (
                            <Fragment>
                                <hr/>
                                <RangeMonthPickerPresets
                                    setRange={this.setRange}
                                    translation={this.props.translation.calendar.monthPickerPresets}
                                />
                                <hr/>
                            </Fragment>
                        )
                    }

                </RangeMonthPickerStyled>

            </DropDownFilter>

        </Fragment>;
    }
}

interface IRangeMonthPicker {
    isSingle?: boolean;
    label: string;
    maxDate?: Moment;
    minDate?: Moment;
    onOkClick?: (rangeStart: Moment, rangeEnd: Moment) => void;
    onSelect?: (range: Moment) => void;
    rangeEnd?: Moment;
    rangeStart?: Moment;
    withPresets?: boolean;
}

interface IConnectedProps {
    translation: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    translation: makeSelectTranslationCommon(),
});

export default connect(mapStateToProps)(RangeMonthPicker);
